/** Small gameplay widgets: rush timer (GP-07.2), combo counter (GP-08), tutorial hand, preview bubble. */
import * as Phaser from 'phaser';
import { app } from '../game/context.js';
import { C, FONT, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { deco, dur } from '../fx/motion.js';
import { t } from '../i18n/index.js';
import { drawnArt } from './looks.js';

export class RushTimer {
  private root: Phaser.GameObjects.Container;
  private bar: Phaser.GameObjects.Graphics;
  private text: Phaser.GameObjects.Text;
  private w: number;
  private h: number;
  private urgent = false;

  constructor(
    private scene: Phaser.Scene,
    x: number,
    y: number,
    w: number,
  ) {
    const u = vp.u;
    this.w = w;
    this.h = 30 * u;
    this.root = scene.add.container(x, y).setDepth(3300);
    const bg = scene.add.graphics();
    bg.fillStyle(C.ink, 1);
    bg.fillRoundedRect(-w / 2, -this.h / 2 + 3 * u, w, this.h, this.h / 2);
    bg.fillStyle(C.cream, 1);
    bg.fillRoundedRect(-w / 2, -this.h / 2, w, this.h, this.h / 2);
    this.bar = scene.add.graphics();
    this.text = scene.add
      .text(0, 0, '', { fontFamily: FONT, fontSize: `${Math.round(18 * u)}px`, fontStyle: '700', color: css(C.ink) })
      .setOrigin(0.5);
    this.root.add([bg, this.bar, this.text]);
  }

  set(leftMs: number, totalMs: number): void {
    const u = vp.u;
    const k = Math.max(0, Math.min(1, leftMs / Math.max(1, totalMs)));
    const s = Math.ceil(Math.max(0, leftMs) / 1000);
    const label = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    if (this.text.text !== label) this.text.setText(label);
    const urgent = s <= 15;
    this.bar.clear();
    this.bar.fillStyle(urgent ? C.danger : C.teal, 1);
    const inset = 4 * u;
    const bw = (this.w - inset * 2) * k;
    if (bw > 1) this.bar.fillRoundedRect(-this.w / 2 + inset, -this.h / 2 + inset, bw, this.h - inset * 2, (this.h - inset * 2) / 2);
    if (urgent && !this.urgent && deco(1)) this.scene.tweens.add({ targets: this.root, scale: 1.08, duration: 300, yoyo: true, repeat: -1 });
    this.urgent = urgent;
  }
}

export class ComboBadge {
  private text: Phaser.GameObjects.Text;
  private sub: Phaser.GameObjects.Text;

  constructor(
    private scene: Phaser.Scene,
    private x: number,
    private y: number,
  ) {
    const u = vp.u;
    this.text = scene.add
      .text(x, y, '', {
        fontFamily: FONT,
        fontSize: `${Math.round(30 * u)}px`,
        fontStyle: '700',
        color: '#ffffff',
        stroke: css(C.tomatoDark),
        strokeThickness: Math.round(7 * u),
      })
      .setOrigin(0.5)
      .setDepth(9600)
      .setAlpha(0);
    this.sub = scene.add
      .text(x, y + 26 * u, '', {
        fontFamily: FONT,
        fontSize: `${Math.round(17 * u)}px`,
        fontStyle: '700',
        color: css(C.mustard),
        stroke: css(C.ink),
        strokeThickness: Math.round(5 * u),
      })
      .setOrigin(0.5)
      .setDepth(9600)
      .setAlpha(0);
  }

  /** GP-08.1: chain step ≥ 1 shows the combo and its +10%/step bonus. */
  show(step: number): void {
    const pct = Math.min(100, step * 10);
    this.text.setText(t('play.combo', { n: step + 1 }));
    this.sub.setText(t('play.comboBonus', { pct }));
    const d = dur(900);
    this.scene.tweens.killTweensOf([this.text, this.sub]);
    this.text.setAlpha(1).setScale(0.4).setY(this.y);
    this.sub.setAlpha(1).setY(this.y + 26 * vp.u);
    if (!d) {
      this.text.setAlpha(0);
      this.sub.setAlpha(0);
      return;
    }
    this.scene.tweens.add({ targets: this.text, scale: 1 + Math.min(0.5, step * 0.08), duration: 220, ease: 'Back.easeOut' });
    this.scene.tweens.add({ targets: [this.text, this.sub], alpha: 0, y: `-=${24 * vp.u}`, delay: d * 0.7, duration: d * 0.3 });
  }
}

/** Tutorial pointing hand (Flow A step C). */
export class HandPointer {
  private g: Phaser.GameObjects.Container;
  private tween: Phaser.Tweens.Tween | null = null;
  private ring: Phaser.GameObjects.Graphics;
  private ringTween: Phaser.Tweens.Tween | null = null;

  constructor(private scene: Phaser.Scene) {
    const u = vp.u;
    const s = 44 * u;
    const g = scene.add.graphics();
    // stylised hand: finger + palm
    g.fillStyle(0xffffff, 1);
    g.lineStyle(3 * u, C.ink, 1);
    g.fillRoundedRect(-s * 0.14, -s * 0.05, s * 0.28, s * 0.62, s * 0.14);
    g.strokeRoundedRect(-s * 0.14, -s * 0.05, s * 0.28, s * 0.62, s * 0.14);
    g.fillRoundedRect(-s * 0.3, s * 0.35, s * 0.72, s * 0.62, s * 0.22);
    g.strokeRoundedRect(-s * 0.3, s * 0.35, s * 0.72, s * 0.62, s * 0.22);
    this.g = scene.add.container(0, 0, [g]).setDepth(9800).setVisible(false);
    this.ring = scene.add.graphics().setDepth(9790).setVisible(false);
  }

  /** Point at (x, y); with `ring` (a tile size) the target also glows. */
  show(x: number, y: number, ring = 0): void {
    this.ringTween?.stop();
    this.ring.clear().setVisible(ring > 0).setPosition(x, y).setScale(1).setAlpha(1);
    if (ring > 0) {
      const u = vp.u;
      this.ring.lineStyle(5 * u, 0xffffff, 1);
      this.ring.strokeRoundedRect(-ring * 0.56, -ring * 0.56, ring * 1.12, ring * 1.12, ring * 0.24);
      this.ring.lineStyle(3 * u, C.mustard, 1);
      this.ring.strokeRoundedRect(-ring * 0.6, -ring * 0.6, ring * 1.2, ring * 1.2, ring * 0.26);
      const d = deco(600);
      if (d) this.ringTween = this.scene.tweens.add({ targets: this.ring, scale: 1.08, alpha: 0.6, duration: d, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      x += ring * 0.12;
      y += ring * 0.1;
    }
    this.g.setVisible(true).setPosition(x, y).setAngle(-18);
    this.tween?.stop();
    const d = deco(520);
    if (d) this.tween = this.scene.tweens.add({ targets: this.g, y: y + 14 * vp.u, scale: 0.9, duration: d, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  hide(): void {
    this.tween?.stop();
    this.tween = null;
    this.ringTween?.stop();
    this.ringTween = null;
    this.g.setVisible(false);
    this.ring.setVisible(false);
  }
}

/** Hold-to-preview bubble (PRD §6 input rules). */
export class PreviewBubble {
  private root: Phaser.GameObjects.Container;
  private img: Phaser.GameObjects.Image;
  private label: Phaser.GameObjects.Text;
  private size: number;

  constructor(private scene: Phaser.Scene) {
    const u = vp.u;
    this.size = 110 * u;
    const g = scene.add.graphics();
    g.fillStyle(C.ink, 0.3);
    g.fillRoundedRect(-this.size / 2, -this.size / 2 + 5 * u, this.size, this.size + 26 * u, 22 * u);
    g.fillStyle(0xffffff, 1);
    g.lineStyle(3 * u, C.ink, 1);
    g.fillRoundedRect(-this.size / 2, -this.size / 2, this.size, this.size + 26 * u, 22 * u);
    g.strokeRoundedRect(-this.size / 2, -this.size / 2, this.size, this.size + 26 * u, 22 * u);
    this.img = scene.add.image(0, -4 * u, '__DEFAULT');
    this.label = scene.add
      .text(0, this.size / 2 + 2 * u, '', { fontFamily: FONT, fontSize: `${Math.round(16 * u)}px`, fontStyle: '700', color: css(C.ink) })
      .setOrigin(0.5);
    this.root = scene.add.container(0, 0, [g, this.img, this.label]).setDepth(9900).setVisible(false);
  }

  show(x: number, y: number, itemId: string | null, name: string): void {
    const f = itemId ? app().art.item(itemId) : app().art.get('prp_mech_wrap');
    this.img.setTexture(f.texture, f.frame);
    this.img.setScale((this.size * 0.78) / Math.max(this.img.width, this.img.height));
    this.label.setText(name);
    const u = vp.u;
    const px = Math.max(this.size / 2 + 8 * u, Math.min(this.scene.scale.width - this.size / 2 - 8 * u, x));
    const py = Math.max(this.size / 2 + 70 * u, y - this.size * 1.05);
    this.root.setPosition(px, py).setVisible(true).setScale(0.6);
    this.scene.tweens.add({ targets: this.root, scale: 1, duration: dur(140) || 1, ease: 'Back.easeOut' });
  }

  hide(): void {
    this.root.setVisible(false);
  }

  get visible(): boolean {
    return this.root.visible;
  }

  /** What the bubble shows now: the asset drawn and its label; null while hidden (test hooks). */
  info(): { art: string; real: boolean; name: string } | null {
    if (!this.root.visible) return null;
    const { id, real } = drawnArt(this.img);
    return { art: id, real, name: this.label.text };
  }
}

/**
 * clearGoal: a banner that slides in across the board ("Now clear the counter!"), holds briefly
 * and slides out. Under skipped animations it is not drawn, but still counted (test hooks).
 */
export class SlideBanner {
  shown = 0;
  private root: Phaser.GameObjects.Container | null = null;

  constructor(private scene: Phaser.Scene) {}

  show(text: string, x: number, y: number, maxW: number): void {
    this.shown++;
    this.root?.destroy();
    this.root = null;
    const d = dur(360);
    if (!d) return;
    const u = vp.u;
    const W = this.scene.scale.width;
    const label = this.scene.add
      .text(0, 0, text, {
        fontFamily: FONT,
        fontSize: `${Math.round(22 * u)}px`,
        fontStyle: '700',
        color: '#ffffff',
        stroke: css(C.ink),
        strokeThickness: Math.round(6 * u),
        align: 'center',
        wordWrap: { width: maxW - 36 * u },
      })
      .setOrigin(0.5);
    const w = Math.min(maxW, label.width + 36 * u);
    const h = label.height + 18 * u;
    const bg = this.scene.add.graphics();
    bg.fillStyle(C.ink, 1);
    bg.fillRoundedRect(-w / 2, -h / 2 + 4 * u, w, h, 16 * u);
    bg.fillStyle(C.teal, 1);
    bg.lineStyle(3 * u, C.ink, 1);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 16 * u);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 16 * u);
    const root = this.scene.add.container(-w, y, [bg, label]).setDepth(9650);
    this.root = root;
    root.setAngle(-3);
    this.scene.tweens.add({ targets: root, x, duration: d, ease: 'Back.easeOut' });
    this.scene.tweens.add({
      targets: root,
      x: W + w,
      delay: d + 1500,
      duration: d * 0.8,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        root.destroy();
        if (this.root === root) this.root = null;
      },
    });
  }

  /** Is the banner on screen now (test hooks)? */
  get visible(): boolean {
    return !!this.root;
  }
}
